import React, { useState, useCallback, useEffect } from "react";
import { LuUpload } from "react-icons/lu";
import {
  Card,
  CardBody,
  Input,
  Option,
  Select,
} from "@material-tailwind/react";
import AnalysedReport from "../AnalysedReport/AnalysedReport";
import AWS from "aws-sdk";

const configureS3 = () => {
  const config = {
    accessKeyId: process.env.REACT_APP_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_SCRET_KEY,
    region: process.env.REACT_APP_REGION,
  };

  AWS.config.update(config);
  return new AWS.S3();
};

const parseCSV = (text) => {
  const rows = text.split("\n");
  const headers = rows[0].split(",").map((header) => header.trim());

  const parseRow = (row) => {
    const values = [];
    let value = "";
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== "\\")) {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(value.trim());
        value = "";
      } else {
        value += char;
      }
    }
    values.push(value.trim());
    return values;
  };

  const parsedData = rows
    .slice(1)
    .map((row) => {
      const values = parseRow(row);
      if (values.length !== headers.length) return null;
      return headers.reduce((obj, header, index) => {
        obj[header] = values[index];
        return obj;
      }, {});
    })
    .filter((row) => row !== null && Object.values(row).some((value) => value));

  return parsedData;
};

const Reports = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const uploadedFileName = localStorage.getItem("uploadedFileName");
  const S3_BUCKET = process.env.REACT_APP_AP_FILE_S3_BUCKET;

  const fetchDataFromS3 = useCallback(
    async (fileName) => {
      const s3 = configureS3();

      try {
        const params = {
          Bucket: S3_BUCKET,
          Key: fileName,
        };
        const response = await s3.getObject(params).promise();
        const text = response.Body.toString("utf-8");
        const parsedData = parseCSV(text);
        setData(parsedData);
        setError(null);
      } catch (err) {
        console.error("Error fetching data from S3:", err);
        setError("Error fetching data from S3. Please try uploading again.");
      }
    },
    [S3_BUCKET]
  );

  const uploadFileToS3 = useCallback(
    async (file) => {
      const s3 = configureS3();

      const uploadParams = {
        Bucket: S3_BUCKET,
        Key: file.name,
        Body: file,
      };

      await s3.upload(uploadParams).promise();
      localStorage.setItem("uploadedFileName", file.name);
    },
    [S3_BUCKET]
  );

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const parsedData = parseCSV(text);
          await uploadFileToS3(file);
          setData(parsedData);
          setError(null);
        } catch (err) {
          setError("Error parsing CSV file. Please check the format.");
        }
      };

      reader.readAsText(file);
    },
    [uploadFileToS3]
  );

  const handleReset = () => {
    setData(null);
    localStorage.removeItem("uploadedFileName");
  };

  const FilterBar = (filters, setFilters, vendors, currencies) => (
    <Card className="mb-6">
      <CardBody className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Select Vendor"
            value={filters?.vendor}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, vendor: value }));
            }}
          >
            {vendors?.map((vendor) => (
              <Option key={vendor} value={vendor}>
                {vendor}
              </Option>
            ))}
          </Select>

          <Select
            label="Select Currency"
            value={filters?.currency}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, currency: value }))
            }
          >
            {currencies?.map((currency) => (
              <Option key={currency} value={currency}>
                {currency}
              </Option>
            ))}
          </Select>

          <Input
            type="date"
            label="Start Date"
            value={filters.dateRange?.start || ""}
            onChange={(e) => {
              const startDate = e.target.value;
              setFilters((prev) => ({
                ...prev,
                dateRange: {
                  start: startDate,
                  end: startDate ? prev.dateRange.end : "",
                },
              }));
            }}
            className="w-full"
          />

          <Input
            type="date"
            label="End Date"
            value={filters.dateRange?.end || ""}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                dateRange: { ...prev.dateRange, end: e.target.value },
              }))
            }
            className="w-full"
            disabled={!filters.dateRange.start}
            min={filters.dateRange.start || ""}
          />
        </div>
      </CardBody>
    </Card>
  );

  useEffect(() => {
    const fileName = localStorage.getItem("uploadedFileName");
    if (fileName) {
      fetchDataFromS3(fileName);
    }
    return () => {
      handleReset();
    };
  }, [fetchDataFromS3]);

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {data ? (
        <AnalysedReport
          data={data}
          handleReset={handleReset}
          FilterBar={FilterBar}
        />
      ) : !uploadedFileName ? (
        <div className="space-y-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">AP Analysis Dashboard</h1>
            <input
              type="file"
              accept=".csv"
              id="csvUpload"
              className="hidden"
              onChange={handleFileUpload}
            />
            <button
              onClick={() => document.getElementById("csvUpload").click()}
              className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-lg text-black hover:bg-gray-100 w-full"
            >
              <LuUpload className="mr-2 h-4 w-4" />
              Import AP Data (CSV)
            </button>
          </div>

          {error && (
            <div className="mb-4 text-red-500">
              <p>{error}</p>
            </div>
          )}

          <div className="border border-gray-300 rounded-lg">
            <p className="p-8 text-center text-gray-500">
              Upload a CSV file to view AP analysis
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center">Fetching Data...</div>
      )}
    </div>
  );
};

export default Reports;
